import * as Api from "@api";
import { Form, Modal } from "@components";
import { ConfirmationModal } from "./confirmation-modal";
import { Box, Grid, Typography, Button } from "@material-ui/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { State } from "@store";
import { TEXTS } from "@utils";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Actions, Section } from "../../../../components";
import { useRequestContext, Values } from "../../../../context";
import { AccessList } from "./access-list";
import { AttestationModal } from "./attestation-modal";
import { getRequestPayload } from "./data";
import { init, reducer } from "./state";
import { SuccessModal } from "./success-modal";
import { UsersList } from "./users-list";
import { postDraft } from "@api/drafts";
import { getDraftPayloadValuesFromRequestContext } from "../../../../utils";
import { SaveDraftModal } from "../../../other-components/saveDraftModal";

export const Summary: React.FC = () => {
  const [requestContextState, requestContextDispatch] = useRequestContext();
  const [
    current_user_name,
    current_user_email,
  ] = useSelector((state: State) => [
    state.user?.name || "",
    state.user?.email || "",
  ]);
  const [state, dispatch] = React.useReducer(reducer, {}, init);

  // [TODO] Move this to reducer
  const [openModalDraft, setOpenModalDraft] = React.useState<boolean>(false);
  const [justificationError, setJustificationError] = React.useState<
    Error | undefined
  >(undefined);
  const [dateError, setDateError] = React.useState<Error | undefined>(
    undefined
  );

  const [attestationError, setAttestationError] = React.useState<
    Error | undefined
  >(undefined);
  const history = useHistory();
  const actions = [
    {
      label: "Cancel",
      handler: () => {
        setOpenModalDraft(false);
      },
    },
    {
      label: "Yes",
      handler: () => {
        postDraft(
          getDraftPayloadValuesFromRequestContext(requestContextState.values)
        ).then(() => {
          history.push("/dashboard/drafts");
        });
      },
    },
  ];

  const declineAttestationActions = [
    {
      label: "Cancel",
      handler: () => {
        dispatch({ type: "DECLINE_ATTESTATION_MODAL:CLOSE" });
        dispatch({ type: "ATTESTATION_MODAL:OPEN" });
      },
    },
    {
      label: "Yes",
      handler: () => {
        history.push("/");
      },
    },
  ];

  const active = React.useMemo(() => {
    const { name, view } = requestContextState.step;

    return name === "summary" && view === "summary";
  }, [requestContextState.step]);

  // Effects

  //   React.useEffect(() => {
  //     if (Object.values(errors).some((error) => !!error)) {
  //       setMessage(messages.required_fields_missings, true);
  //     }
  //   }, [errors, setMessage]);

  React.useEffect(() => {
    if (active && !requestContextState.values.systems.length) {
      requestContextDispatch({
        type: "set-step",
        name: "system-details",
        view: "select-systems",
      });
    }
  }, [
    active,
    requestContextState.values.systems.length,
    requestContextDispatch,
  ]);

  // Handlers

  function handleJustificationValueChange(
    name: "justification",
    value: string
  ): void {
    validate(name, value).then(
      ([, error]) =>
        requestContextDispatch({
          type: "set-justification",
          justification: value,
        })
      // [TODO] Handle error
    );
  }

  function handleDateValueChange(
    name: "date",
    value: MaterialUiPickersDate
  ): void {
    validate(name, value as Date).then(
      (
        [, error] // [Confirm assertion]
      ) =>
        requestContextDispatch({
          type: "set-date",
          date: value as Date, // [Confirm assertion]
        })
      // [TODO] Handle error
    );
  }

  function handleAttestationModalValueChange(
    name: "attestation",
    value: string
  ): void {
    requestContextDispatch({
      type: "set-attestation",
      attestation: value,
    });
  }

  function handleAttestationModalClose(): void {
    dispatch({
      type: "ATTESTATION_MODAL:CLOSE",
    });

    requestContextDispatch({
      type: "set-attestation",
      attestation: "",
    });
  }

  function handleAttestationModalDecline(): void {
    requestContextDispatch({
      type: "set-attestation",
      attestation: "",
    });
    dispatch({
      type: "ATTESTATION_MODAL:CLOSE",
    });
    dispatch({
      type: "DECLINE_ATTESTATION_MODAL:OPEN",
    });
  }

  function handleAttestationModalAccept(): void {
    validate("attestation", requestContextState.values.attestation).then(
      ([, error]) => {
        if (error) {
          setAttestationError(error);
        } else {
          setAttestationError(undefined);
          dispatch({
            type: "ATTESTATION_MODAL:CLOSE",
          });
          submit();
        }
      }
    );
  }

  function handlePreviousButtonClick(): void {
    requestContextDispatch({
      type: "set-step",
      name: "system-details",
      view: "systems-list",
    });
  }

  function handleSaveAsDraftButtonClick(): void {
    setOpenModalDraft(true); // [TODO] Refactor
  }

  function handleSubmitRequestButtonClick(): void {
    submit();
  }

  function handleConfirmationModalCancel(): void {
    dispatch({
      type: "CONFIRMATION_MODAL:CLOSE",
    });
  }

  function handleConfirmationModalConfirm(): void {
    dispatch({
      type: "CONFIRMATION_MODAL:CLOSE",
    });
    dispatch({
      type: "IN_PROGRESS:TOGGLE",
      value: true,
    });
    Api.postRequest(getRequestPayload(requestContextState.values))
      .then((numbers) => {
        dispatch({
          type: "IN_PROGRESS:TOGGLE",
          value: false,
        });
        dispatch({
          type: "SUCCESS_MODAL:OPEN",
          numbers,
        });
      })
      .catch((err) => dispatch({ type: "ERROR_MODAL:OPEN" }));
  }

  // Helpers
  function shouldShowUsersList(): boolean {
    return requestContextState.values.target === "MULTIPLE";
  }

  function shouldShowSystemsList(): boolean {
    return !!requestContextState.values.systems.length;
  }

  function isMobileAttestation(): boolean {
    return requestContextState.values.systems.some(
      (system) => system.application.mobileAttestation
    );
  }

  function isSubmitRequestButtonDisabled(): boolean {
    return (
      !requestContextState.values.users.length ||
      !requestContextState.values.systems.reduce(
        (accesses, system) => accesses + system.accesses.length,
        0
      )
    );
  }

  // Validations

  function validate<K extends keyof Values>(
    name: K,
    value: Values[K]
  ): Promise<[keyof Values, Error?]> {
    let error;

    switch (name) {
      case "attestation":
        // error = Form.Validators.isEqual(value, current_user_name);
        break;
      case "date":
        error = Form.Validators.hasValue(value);
        setDateError(error);
        break;
      case "justification":
        var isStringWhitespaceOnly =
          value !== undefined && validateWhitespaceString(value.toString());
        error = Form.Validators.hasValue(value);

        if (isStringWhitespaceOnly) {
          error = Form.Validators.hasValue(undefined);
          setJustificationError(error);
        } else setJustificationError(error);

        break;
      case "type":
      case "users":
        error = Form.Validators.hasValue(value);
        break;
      case "systems": {
        error = Form.Validators.hasValue(value);
        // [TODO] Validate additional forms
        // if (
        //   !error &&
        //   (value as System[]).some(({ form, application }) => {
        //     return (
        //       !form &&
        //       !application.manual &&
        //       (values.type !== "REVOKE_ACCESS" || application.revokeNeededForm)
        //     );
        //   })
        // ) {
        //   error = new Error("Additional forms are required");
        // }
        break;
      }
    }

    return Promise.resolve([name, error]);
  }

  function validateWhitespaceString(value: string) {
    var isWhitespaceOnly = true;
    var character;

    for (
      var stringIterator = 0;
      stringIterator < value.length;
      stringIterator++
    ) {
      character = value.charAt(stringIterator);

      if (character !== " ") {
        isWhitespaceOnly = false;
        break;
      }
    }

    return isWhitespaceOnly;
  }

  // Submit

  async function submit() {
    // [TODO] Refactor this
    if (
      requestContextState.values.systems.some((system) => {
        const { additionalForm, manual, revokeNeededForm } = system.application;
        const type = requestContextState.values.type;

        return system.accesses.some((access) => {
          return (
            !!additionalForm &&
            !manual &&
            (type !== "revoke" || revokeNeededForm) &&
            !access.form
          );
        });
      })
    ) {
      // [TODO] There are pending additional forms
      return;
    }

    const names = (Object.keys(requestContextState.values) as Array<
      keyof Values
    >).filter((name) => name !== "attestation");

    const userIsIncluded = requestContextState.values.users.find(
      (user) => user.email === current_user_email
    );
    Promise.all(
      names.map((name) => validate(name, requestContextState.values[name]))
    )
      .then((validations) =>
        validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true)
      )
      .then((valid) => {
        if (valid) {
          validate("attestation", requestContextState.values.attestation).then(
            ([, error]) => {
              if (error && !!userIsIncluded) {
                dispatch({
                  type: "ATTESTATION_MODAL:OPEN",
                });
              } else {
                handleConfirmationModalConfirm();
              }
            }
          );
        }
      });
  }

  const closeSaveDraftModal = () => {
    setOpenModalDraft(false);
  };

  return (
    <Section active={active}>
      <Grid container spacing={2} component={Box}>
        <Grid item xs={12}>
          <Form>
            <Form.Textarea
              error={justificationError}
              label={{
                text: "Access Justification",
                caption: "Please explain why the access is needed",
              }}
              maxLength={1000}
              name="justification"
              onChange={handleJustificationValueChange}
              placeholder="Please limit your answer to 1000 characters only"
              required
              rows={5}
              rowsMax={5}
              size={4}
              value={requestContextState.values.justification}
            />
            <Form.Datebox
              disablePast
              error={dateError}
              label={{
                text: "Request's Effective Date",
                tooltip: "Date when the access should be granted/revoked",
              }}
              name="date"
              onChange={handleDateValueChange}
              required
              size={4}
              value={requestContextState.values.date}
            />
          </Form>
        </Grid>
      </Grid>
      <UsersList visible={shouldShowUsersList()} />
      <AccessList visible={shouldShowSystemsList()} />
      <Grid container spacing={2} component={Box} pt={2}>
        <Grid item xs={12}>
          <Actions>
            <Actions.Cancel />
            <Actions.Previous onClick={handlePreviousButtonClick} />
            <Actions.SaveAsDraft onClick={handleSaveAsDraftButtonClick} />
            <Actions.Submit
              disabled={isSubmitRequestButtonDisabled()}
              onClick={handleSubmitRequestButtonClick}
              pending={state.in_progress}
            />
          </Actions>
        </Grid>
      </Grid>
      <AttestationModal
        error={attestationError} // [TODO] Get error
        mobile={isMobileAttestation()}
        name={current_user_name}
        open={state.attestation_modal.open}
        onAccept={handleAttestationModalAccept}
        onChange={handleAttestationModalValueChange}
        onDecline={handleAttestationModalDecline}
        onClose={handleAttestationModalClose}
        isAttesting={false}
        value={requestContextState.values.attestation || ""}
      />
      <ConfirmationModal
        onCancel={handleConfirmationModalCancel}
        onConfirm={handleConfirmationModalConfirm}
        open={state.confirmation_modal.open}
      />
      <SuccessModal
        numbers={state.success_modal.numbers}
        open={state.success_modal.open}
      />
      <Modal
        isOpen={state.decline_attestation_modal.open}
        actions={declineAttestationActions}
      >
        <Typography variant="body1" paragraph>
          {TEXTS.DECLINE_ATTESTATION_CONFIRMATION}
        </Typography>
      </Modal>
      <Modal
        isOpen={state.error_modal.open}
        actions={[
          {
            label: "OK",
            handler: () => {
              dispatch({
                type: "IN_PROGRESS:TOGGLE",
                value: false,
              });

              dispatch({ type: "ERROR_MODAL:CLOSE" });
            },
          },
        ]}
      >
        <Typography variant="body1" paragraph>
          There was an Error submiting your request
        </Typography>
      </Modal>
      <SaveDraftModal
        isShowDraft={openModalDraft}
        data={requestContextState.values}
        closeSaveDraftModal={closeSaveDraftModal}
      />
    </Section>
  );
};
